import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { RefreshCw, Search, Wand2 } from 'lucide-react';
import { apiService } from '../../../services/api';
import { useToast } from '../../../contexts/ToastContext';
import Avatar from '../../epics/Avatar';
import FilterChip from '../../flow/FilterChip';
import { FLOW_ROLES, FlowRole, roleLabel } from '../../flow/types';
import RoleToggle from './RoleToggle';
import * as fmt from '../../../lib/format';

interface Person {
  account_id: string;
  name: string | null;
  email: string | null;
  groups: string[];
  roles: FlowRole[];
  suggested: FlowRole[];
  moves_90d: number;
}
interface Meta { without_role: number; recent_without_role: number; suggestions_available: number }

type Filter = 'all' | 'none' | 'recent';

const CARD = 'rounded-2xl bg-white shadow-sm ring-1 ring-gray-200/70 contrast-more:ring-gray-400';

function PersonRow({ person, onChange }: { person: Person; onChange: (roles: FlowRole[]) => Promise<void> }) {
  const [saving, setSaving] = useState(false);

  const set = async (roles: FlowRole[]) => {
    setSaving(true);
    try {
      await onChange(roles);
    } finally {
      setSaving(false);
    }
  };
  const toggle = (role: FlowRole) =>
    set(person.roles.includes(role) ? person.roles.filter((r) => r !== role) : [...person.roles, role]);

  return (
    <li className="flex flex-wrap items-center gap-x-4 gap-y-2.5 px-5 py-3.5">
      <div className="flex min-w-0 basis-64 items-center gap-3">
        <Avatar name={person.name} />
        <div className="min-w-0">
          <div className="truncate text-sm font-medium text-gray-900">{person.name ?? person.account_id}</div>
          <div className="truncate text-xs text-gray-500" title={person.groups.join(', ')}>
            {person.groups.length > 0 ? person.groups.slice(0, 3).join(' · ') + (person.groups.length > 3 ? ` · +${person.groups.length - 3}` : '') : 'No Jira groups'}
          </div>
        </div>
      </div>
      <div className="w-24 text-xs tabular-nums text-gray-500">
        {person.moves_90d > 0 ? `${fmt.count(person.moves_90d)} moves` : 'No recent moves'}
      </div>
      <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5" role="group" aria-label={`Roles for ${person.name ?? person.account_id}`}>
        {FLOW_ROLES.map((r) => (
          <RoleToggle key={r.key} label={r.label} tone={r.tone} on={person.roles.includes(r.key)} disabled={saving} onClick={() => void toggle(r.key)} />
        ))}
        {person.roles.length === 0 && person.suggested.length > 0 && (
          <button
            type="button"
            disabled={saving}
            onClick={() => void set(person.suggested)}
            title="Taken from this person's Jira groups"
            className="ml-1 inline-flex items-center gap-1 rounded-full px-2.5 py-1.5 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-300 transition-[transform,background-color] duration-150 hover:bg-blue-50 active:scale-[0.96] disabled:opacity-60 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500"
          >
            <Wand2 className="h-3 w-3" aria-hidden />
            Use {person.suggested.map(roleLabel).join(' + ')}
          </button>
        )}
      </div>
    </li>
  );
}

// Who has which function. A move is judged only when the person who made it has a role here.
export default function PeopleRoles() {
  const { showToast } = useToast();
  const [people, setPeople] = useState<Person[]>([]);
  const [meta, setMeta] = useState<Meta | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('recent');
  const [query, setQuery] = useState('');
  const [applying, setApplying] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await apiService.getFlowRoles();
      if (r.success) {
        setPeople(r.data ?? []);
        setMeta(r.meta ?? null);
        setError(null);
      } else setError(r.error || 'Could not load people');
    } catch (e: any) {
      setError(e?.response?.data?.error || 'Could not load people');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { void load(); }, [load]);

  const saveRoles = async (person: Person, roles: FlowRole[]) => {
    const before = people;
    setPeople((list) => list.map((p) => (p.account_id === person.account_id ? { ...p, roles } : p)));
    try {
      const r = await apiService.setFlowRoles(person.account_id, roles);
      if (!r.success) throw new Error(r.error || 'Could not save');
      void load();
    } catch (e: any) {
      setPeople(before);
      showToast('error', e?.response?.data?.error || e?.message || 'Could not save the roles');
    }
  };

  const applySuggestions = async () => {
    setApplying(true);
    try {
      const r = await apiService.applyFlowSuggestions();
      if (r.success) {
        showToast('success', r.data.applied === 0 ? 'Nobody needed a role from their groups' : `Gave ${r.data.applied} ${r.data.applied === 1 ? 'person' : 'people'} the role their Jira groups suggest`);
        await load();
      } else showToast('error', r.error || 'Could not apply the suggestions');
    } catch (e: any) {
      showToast('error', e?.response?.data?.error || 'Could not apply the suggestions');
    } finally {
      setApplying(false);
    }
  };

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return people.filter((p) => {
      if (filter === 'none' && p.roles.length > 0) return false;
      if (filter === 'recent' && p.moves_90d === 0) return false;
      if (!q) return true;
      return (p.name ?? '').toLowerCase().includes(q) || (p.email ?? '').toLowerCase().includes(q);
    });
  }, [people, filter, query]);

  return (
    <section className={CARD} aria-label="People and roles">
      <div className="border-b border-gray-100 p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="max-w-2xl">
            <h3 className="text-sm font-semibold text-gray-900">People and roles</h3>
            <p className="mt-1 text-sm text-gray-600">
              Flow judges a move only when the person who made it has a role. A person can hold several. People who have not moved a ticket in 90 days are listed after the active ones.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button type="button" aria-label="Reload" onClick={() => void load()} className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 active:scale-[0.96] motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500">
              <RefreshCw className="h-4 w-4" />
            </button>
            <button
              type="button"
              disabled={applying || !meta || meta.suggestions_available === 0}
              aria-busy={applying}
              onClick={() => void applySuggestions()}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3.5 py-2 text-sm font-medium text-white shadow-sm transition-[background-color,transform] duration-150 hover:bg-blue-700 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2"
            >
              <Wand2 className="h-4 w-4" aria-hidden />
              {applying ? 'Filling…' : `Fill from Jira groups${meta ? ` (${meta.suggestions_available})` : ''}`}
            </button>
          </div>
        </div>
        {meta && (
          <p className="mt-3 text-xs text-gray-500">
            {people.length} people · {meta.without_role} without a role · {meta.recent_without_role} of them moved tickets in the last 90 days. Filling from groups only touches people who have no role yet, and every role stays editable.
          </p>
        )}
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <FilterChip label="Moved tickets recently" selected={filter === 'recent'} onClick={() => setFilter('recent')} />
          <FilterChip label="No role yet" selected={filter === 'none'} onClick={() => setFilter('none')} />
          <FilterChip label="Everyone" selected={filter === 'all'} onClick={() => setFilter('all')} />
          <label className="relative ml-auto">
            <span className="sr-only">Search people</span>
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-500" aria-hidden />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people"
              className="w-56 rounded-lg border border-gray-200 bg-white py-1.5 pl-8 pr-3 text-sm text-gray-900 placeholder:text-gray-500 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
            />
          </label>
        </div>
      </div>

      {error && <div className="m-5 rounded-xl bg-rose-50 px-4 py-2.5 text-sm text-rose-700 ring-1 ring-rose-200" role="alert">{error}</div>}

      {loading ? (
        <div className="divide-y divide-gray-100" role="status" aria-label="Loading people">
          {[0, 1, 2, 3].map((i) => <div key={i} className="h-16 animate-pulse bg-gray-50/60 motion-reduce:animate-none" />)}
        </div>
      ) : shown.length === 0 ? (
        <div className="px-5 py-12 text-center text-sm text-gray-500">
          {filter === 'none' ? 'Everyone here has a role.' : query ? 'No one matches that search.' : 'No one has moved a ticket in the last 90 days.'}
        </div>
      ) : (
        <ul className="divide-y divide-gray-100">
          {shown.map((p) => (
            <PersonRow key={p.account_id} person={p} onChange={(roles) => saveRoles(p, roles)} />
          ))}
        </ul>
      )}
    </section>
  );
}
